import React from 'react';
import Axios from 'axios';
import PropTypes from 'prop-types';

import { JobApplyContext } from './ContextCreator';
import { PageContext } from './ContextCreator';
import { getIsElementInObjectKeys } from '../utilities/helperFunctions';

export const jobAppStatuses = {
    rejected: 'APPLICATION DECLINED',
    archived: 'APPLICATION ARCHIVED',
    applied: 'APPLICATION SUBMITTED',
    expired: 'JOB EXPIRED',
    new: 'New',
    activelyHiring: 'Actively Hiring',
    default: '',
};

const apiCalls = {
    APPLY_TO_JOB_API: '/api/job-seeker/apply',
    REJECT_APPLICANT_API: '/api/job-seeker/reject',
    SAVE_APPLICANT_API: '/api/job-seeker/save',
};

const sendErrorObj = (errorResponse = {}, message) => {
    if (errorResponse.data) {
        return errorResponse.data;
    } else {
        return {
            meta: {
                success: false,
            },
            data: {},
            errors: {
                _general: [message],
            },
        };
    }
};

export class JobApplyProvider extends React.Component {
    constructor(props, context) {
        super(props);

        this.quickApplyCompanies =
            context.pageProps && context.pageProps.quickApplyCompanies
                ? context.pageProps.quickApplyCompanies
                : {};

        this.state = {
            hasUserApplied: props.hasUserApplied,
            applyToJob: this.applyToJob,
            rejectApplicant: this.rejectApplicant,
            saveApplicant: this.saveApplicant,
            getIsQuickApplyCompany: this.getIsQuickApplyCompany,
            rejectedApplicants: [],
            updateRejectedApplicants: this.updateRejectedApplicants,
        };
    }

    static propTypes = {
        /**
         * whether user has applied or not for the current job on single job page
         */
        hasUserApplied: PropTypes.bool,
    };

    static defaultProps = {
        hasUserApplied: false,
    };

    static contextType = PageContext;

    applyToJob = async (jobId, jobLocationId) => {
        const { hasUserApplied } = this.state;

        if (hasUserApplied) {
            return;
        }

        try {
            const payload = { jobLocationId };
            const { data } =
                (await Axios.post(
                    `${apiCalls.APPLY_TO_JOB_API}/${jobId}`,
                    payload
                )) || {};

            if (data.meta && data.meta.success) {
                this.setState({ hasUserApplied: true });
            }

            return data || {};
        } catch (e) {
            return sendErrorObj(e.response, 'Unable to apply to company.');
        }
    };

    rejectApplicant = async (jobApplicationId) => {
        try {
            const { data } =
                (await Axios.get(
                    `${apiCalls.REJECT_APPLICANT_API}/${jobApplicationId}`
                )) || {};

            return {
                meta: {
                    success: true,
                },
                data,
                errors: {},
            };
        } catch (e) {
            return sendErrorObj(e.response, 'Unable to reject applicant.');
        }
    };

    saveApplicant = async (companyId, jobProfileHash, saved) => {
        const shouldSave = saved ? 1 : 0;

        try {
            const payload = { companyId, jobProfileHash, saved: shouldSave };
            const { data } =
                (await Axios.post(apiCalls.SAVE_APPLICANT_API, payload)) || {};

            return data || {};
        } catch (e) {
            return sendErrorObj(
                e.response,
                'Unable to save or unsave applicant.'
            );
        }
    };

    getIsQuickApplyCompany = (companyId) =>
        getIsElementInObjectKeys(this.quickApplyCompanies, companyId);

    updateRejectedApplicants = (rowNumber) => {
        const newRejectedApplicants = [
            ...this.state.rejectedApplicants,
            rowNumber,
        ];
        this.setState({ rejectedApplicants: newRejectedApplicants });
    };

    render() {
        return (
            <JobApplyContext.Provider value={this.state}>
                {this.props.children}
            </JobApplyContext.Provider>
        );
    }
}

export const JobApplyConsumer = JobApplyContext.Consumer;
